import {createGraphiQLFetcher} from "@graphiql/toolkit";
import {useEffect, useRef, useState} from "react";
import {buildClientSchema, getIntrospectionQuery, parse, print} from "graphql";
import {useExplorerPlugin} from "@graphiql/plugin-explorer";

import getEnvironment from "../../utils/get-environment";
import {fetchGraphql} from "../../utils";
import {AppliedDirectivesQuery} from "../../utils/applied-directives-query";
import GraphiQL from "graphiql";
import getAnalyticsClient from "../../utils/analytics-client";
import {filterSchemaByApiGroup, getApiGroups} from "../../utils/api-groups";
import ErrorBoundary from "./error-boundary/ErrorBoundary";
import FilterMenu from "./filter-menu/FilterMenu";
import "graphiql/graphiql.min.css";
import "@graphiql/plugin-explorer/dist/style.css";
import {useSearchParams} from "react-router-dom";

import "./styles.css";
import {createClient} from "graphql-ws";
import CopyLinkButton from "./copy-link-button";
import ServiceCallVisualisationPlugin, {ServiceCallVisualisationIcon} from "./service-call-visualisation";

const analyticsClient = getAnalyticsClient();

const prettifyGQLWithDefault = (string, defaultString) => {
    try {
        return print(parse(string))
    } catch {
        return defaultString;
    }
};
const prettifyJSONWithDefault = (string, defaultString) => {
    try {
        return JSON.stringify(
            JSON.parse(string),
            null,
            2,
          )
    } catch {
        return defaultString;
    }
};

function Content(props) {
    let [searchParams, setSearchParams] = useSearchParams();
    const initialQuery = useRef(searchParams.get("query") ?? '')
    const initialVariables = useRef(searchParams.get("variables") ?? '')
    const initialHeader = useRef(searchParams.get("header") ?? '')
    // set initial values for query, variables and headers. Attempt to prettify values.
    const [query, setQuery] = useState(prettifyGQLWithDefault(initialQuery.current, initialQuery.current));
    const [variables] = useState(prettifyJSONWithDefault(initialVariables.current ,initialVariables.current ));
    const [headers] = useState(prettifyJSONWithDefault(initialHeader.current, initialHeader.current));

    const [schema, setSchema] = useState(null);
    const [introspectionData, setIntrospectionData] = useState(null);
    const [apiGroups, setApiGroups] = useState(Object.create(null));
    const [selectedService, setSelectedService] = useState("ALL");

    useEffect(() => {
        //Clear the search bar on load
        searchParams.delete("query")
        searchParams.delete("variables")
        searchParams.delete("header")
        setSearchParams(searchParams);
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    const customFetcher = (
        graphQLParams,
        opts = { headers: {}, dontLog: false }
    ) => {
        let headers = opts.headers;
        if (typeof headers === "string") {
            headers = JSON.parse(opts.headers);
        }

        if (!opts.dontLog) {
            analyticsClient.sendOperationalEvent({
                source: "MainMenu",
                action: "executed", // required
                actionSubject: "query", // required
                attributes: { selectedApiGroup: selectedService },
            });
        }

        return fetchGraphql({
            query: JSON.stringify(graphQLParams),
            headers,
        });
    };

    useEffect(() => {
        customFetcher(
            {
                query: getIntrospectionQuery({
                    inputValueDeprecation: true,
                }),
            },
            { dontLog: true },
        ).then(
            (result) => {
                const introspectionData = result.data;
                const schema = buildClientSchema(result.data);
                setSchema(schema);
                setIntrospectionData(introspectionData);
            }
        );
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        customFetcher({ query: AppliedDirectivesQuery }, { dontLog: true }).then(
            (result) => {
                let introspectionResult = result.data;
                setApiGroups(getApiGroups(introspectionResult));
            }
        );
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    const handleServiceSelected = (service) => {
        let schema = buildClientSchema(introspectionData);

        if (service === "ALL") {
            setSchema(schema);
            setSelectedService(service);
            return;
        }

        schema = filterSchemaByApiGroup(schema, apiGroups, service);

        setSchema(schema);
        setSelectedService(service);
        analyticsClient.sendUIEvent({
            source: "Toolbar",
            action: "selected", // required
            actionSubject: "apiGroup", // required
            actionSubjectId: service,
        });
    };

    const wsClient = createClient({
        url: getEnvironment().graphQLSubscriptionEndpoint,
        keepAlive: 570000, // Send graphql-ws PING every 9.5 minutes to enable Atlassian PubSub UCT refresh
    });

    const fetcher = createGraphiQLFetcher({
        url: getEnvironment().graphQLEndpoint,
        wsClient: wsClient,
        headers: {
            credentials: "same-origin",
            Accept: 'multipart/mixed; deferSpec=20220824'},
    });

    const explorerPlugin = useExplorerPlugin({ query: query, onEdit: setQuery });

    const plugins = [
        explorerPlugin,
        {
            icon: () => (
                <div className="api-group-text">{selectedService} selected</div>
            ),
            content: () => (
                <FilterMenu
                    services={apiGroups}
                    onSelectedService={handleServiceSelected}
                    selectedService={selectedService}
                />
            ),
            title: "API Groups",
        },
    ]

    if (getEnvironment().envType !== "PROD") {
        plugins.push(
            {
                icon: () => (
                    <ServiceCallVisualisationIcon/>
                ),
                content: () => (
                    <ServiceCallVisualisationPlugin/>
                ),
                title: "Service call visualisation",
            }
        )
    }

    return (
        <ErrorBoundary
            analyticsClient={analyticsClient}
            selectedService={selectedService}
            query={query}
        >
            <div className="ide-container">
                <GraphiQL
                    key={JSON.stringify(schema)}
                    fetcher={fetcher}
                    schema={schema}
                    query={query}
                    headers={headers}
                    variables={variables}
                    shouldPersistHeaders={true}
                    inputValueDeprecation={true}
                    toolbar={{
                        additionalContent: <CopyLinkButton />,
                    }}
                    plugins={plugins}
                />
            </div>
        </ErrorBoundary>
    );
}

export default Content;
